import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { ChevronRight } from 'lucide-react';

// ✅ Config Imports
import { IS_DEV, TEST_REAL_API } from "@/config/env";

// ✅ Validation Imports
import { qualificationSchema, MEDICAL_DEGREES } from './doctorValidation';
import type { QualificationData } from './doctorValidation';
import type { RootState } from '@/store/store';

// ✅ API Import
import { saveDoctorQualification } from '../../../../../api/onboardingApis/doctorOnboardingApi/doctorQualification.api';

interface DoctorStep2Props {
  onNext: (data: QualificationData) => void;
}

export default function DoctorStep2({ onNext }: DoctorStep2Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 1. Get Personal Data from Redux
  const registrationData = useSelector((state: RootState) => state.expert.registrationData);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<QualificationData>({
    resolver: zodResolver(qualificationSchema),
    mode: "onChange", 
  });

  // ⚡ DEV SKIP HANDLER
  const handleDevSkip = async () => {
   
    await new Promise(resolve => setTimeout(resolve, 800));

    const mockData: QualificationData = {
      degree: 'MBBS',
      college: 'All India Institute of Medical Sciences',
      yearOfCompletion: '2018',
      yearOfExperience: '5'
    };

    toast.success("⚡ Mock Qualification Saved (Dev Mode)");
    onNext(mockData);
  };

  // 🟢 SUBMIT HANDLER
  const onSubmit = async (data: QualificationData) => {
    setIsSubmitting(true);
    try {
      // 🟢 MOCK LOGIC (If Dev & NOT testing Real API)
      if (IS_DEV && !TEST_REAL_API) {
         await handleDevSkip();
         setIsSubmitting(false);
         return; 
      }

      // --- REAL API LOGIC ---
    

      const expertCode = localStorage.getItem('expertCode');
      
      if (!expertCode) {
        toast.error("Session missing. Please login again.");
        setIsSubmitting(false);
        return;
      }

      // Prepare FormData
      const formData = new FormData();
      formData.append('expertCode', expertCode);
      
      if (registrationData) {
        formData.append('name', registrationData.fullName || '');
        formData.append('email', registrationData.email || '');
        
        // 🔥 CRITICAL FIX: You must append the phone number here!
        // The backend expects 'phoneNumber' to match the DTO field we added.
        formData.append('phoneNumber', registrationData.phone || ''); 
      }

      formData.append('degree', data.degree || '');
      formData.append('college', data.college || '');
      formData.append('yearOfCompletion', data.yearOfCompletion || '');
      formData.append('yearOfExperience', data.yearOfExperience || '');

      // 🟢 REAL API CALL
      await saveDoctorQualification(formData);

      toast.success("Qualification details saved!");
      onNext(data);

    } catch (error: any) {
      console.error("API Error:", error);
      toast.error(error.response?.data?.message || "Failed to save qualification. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to determine if we are in "Strict Mode"
  const isStrictMode = !IS_DEV || TEST_REAL_API;

  return (
    <div className="bg-white rounded-lg shadow-xl p-8">
      
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Basic Level Qualification</h2>
        
        {TEST_REAL_API && IS_DEV && (
          <div className="bg-yellow-500/20 text-yellow-700 py-1 px-3 rounded-full text-xs border border-yellow-500/50 font-medium">
            ⚠️ Real API Testing Enabled
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Degree */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Degree {isStrictMode && <span className="text-red-500">*</span>}
          </label>
          <select
            {...register("degree")}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.degree ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Type & select degree</option>
            {MEDICAL_DEGREES.map((degree) => (
              <option key={degree} value={degree}>{degree}</option>
            ))}
          </select>
          {errors.degree && <p className="mt-1 text-sm text-red-600">{errors.degree.message}</p>}
        </div>

        {/* College */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            College/Institute {isStrictMode && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            {...register("college")}
            placeholder="Type college/institute name (write full name)"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.college ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.college && <p className="mt-1 text-sm text-red-600">{errors.college.message}</p>}
        </div>

        {/* Year of Completion */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Year of completion {isStrictMode && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text" 
            inputMode="numeric"
            {...register("yearOfCompletion")}
            onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
            }}
            placeholder="Type year of completion"
            maxLength={4}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.yearOfCompletion ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.yearOfCompletion && <p className="mt-1 text-sm text-red-600">{errors.yearOfCompletion.message}</p>}
        </div>

        {/* Experience */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Year of experience {isStrictMode && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            inputMode="numeric"
            {...register("yearOfExperience")}
            onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
            }}
            placeholder="Type year of experience (between 2-70 years)"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.yearOfExperience ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.yearOfExperience && <p className="mt-1 text-sm text-red-600">{errors.yearOfExperience.message}</p>}
        </div>
      </div>

      <div className="text-center mt-8 space-y-3">
        
        {/* ⚡ DEV BUTTON */}
        {IS_DEV && !TEST_REAL_API && (
            <button
              type="button"
              onClick={handleDevSkip}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-2 px-12 rounded-lg transition-colors border-2 border-yellow-600 border-dashed"
            >
              ⚡ DEV MODE: Skip & Mock Verify
            </button>
        )}

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || (isStrictMode && !isValid)}
          className={`w-full bg-cyan-400 hover:bg-cyan-500 text-white font-semibold py-3 px-12 rounded-lg transition-colors 
            ${(isSubmitting || (isStrictMode && !isValid)) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isSubmitting ? "Processing..." : (
            <span className="flex items-center justify-center gap-2">
              Proceed to Next Step <ChevronRight size={20} />
            </span>
          )}
        </button>
      </div>
    </div>
  );
}